
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function QuoteSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    propertyType: '',
    bedroomsBathrooms: '',
    squareFootage: '',
    frequency: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
    additionalNotes: '',
    smsOptIn: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d3jsmtvb6m158lvbnaa0', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          serviceType: '',
          propertyType: '',
          bedroomsBathrooms: '',
          squareFootage: '',
          frequency: '',
          preferredDate: '',
          preferredTime: '',
          message: '',
          additionalNotes: '',
          smsOptIn: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Get Your Free Quote</h2>
            <p className="text-xl text-gray-600">
              Tell us about your cleaning needs and we'll provide you with a customized quote within 24 hours.
            </p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-peach-50 rounded-2xl p-8 md:p-12">
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Enter your phone number"
                  />
                </div>
                <div>
                  <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-2">
                    Service Type *
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select service type</option>
                    <option value="residential">Residential Cleaning</option>
                    <option value="commercial">Commercial Cleaning</option>
                    <option value="deep-cleaning">Deep Cleaning</option>
                    <option value="move-in-out">Move In/Out Cleaning</option>
                    <option value="post-construction">Post-Construction Cleanup</option>
                    <option value="carpet-cleaning">Carpet Cleaning</option>
                    <option value="window-cleaning">Window Cleaning</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 mb-2">
                    Property Type
                  </label>
                  <select
                    id="propertyType"
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select property type</option>
                    <option value="apartment">Apartment</option>
                    <option value="house">House</option>
                    <option value="condo">Condo</option>
                    <option value="office">Office</option>
                    <option value="retail">Retail Space</option>
                    <option value="medical">Medical Facility</option>
                    <option value="restaurant">Restaurant</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="bedroomsBathrooms" className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Bedrooms and Bathrooms
                  </label>
                  <select
                    id="bedroomsBathrooms"
                    name="bedroomsBathrooms"
                    value={formData.bedroomsBathrooms}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select bedrooms & bathrooms</option>
                    <option value="1-bedroom-1-bathroom">1 Bedroom, 1 Bathroom</option>
                    <option value="2-bedrooms-2-bathrooms">2 Bedrooms, 2 Bathrooms</option>
                    <option value="3-bedrooms-2-bathrooms">3 Bedrooms, 2 Bathrooms</option>
                    <option value="4-bedrooms-2-4-bathrooms">4 Bedrooms, 2–4 Bathrooms</option>
                    <option value="5-bedrooms-2-4-bathrooms">5 Bedrooms, 2–4 Bathrooms</option>
                    <option value="6-bedrooms-3-5-bathrooms">6 Bedrooms, 3–5 Bathrooms</option>
                    <option value="other">Other — Please specify</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="squareFootage" className="block text-sm font-medium text-gray-700 mb-2">
                  Square Footage
                </label>
                <select
                  id="squareFootage"
                  name="squareFootage"
                  value={formData.squareFootage}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">Select square footage</option>
                  <option value="under-1000">Under 1,000 sq ft</option>
                  <option value="1000-2000">1,000 - 2,000 sq ft</option>
                  <option value="2000-3000">2,000 - 3,000 sq ft</option>
                  <option value="3000-4000">3,000 - 4,000 sq ft</option>
                  <option value="over-4000">Over 4,000 sq ft</option>
                </select>
              </div>

              <div>
                <label htmlFor="frequency" className="block text-sm font-medium text-gray-700 mb-2">
                  Cleaning Frequency
                </label>
                <select
                  id="frequency"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">Select frequency</option>
                  <option value="one-time">One-time cleaning</option>
                  <option value="weekly">Weekly</option>
                  <option value="bi-weekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    id="preferredDate"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="preferredTime" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Time
                  </label>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select preferred time</option>
                    <option value="morning-8am-12pm">Morning (8:00 AM - 12:00 PM)</option>
                    <option value="afternoon-12pm-5pm">Afternoon (12:00 PM - 5:00 PM)</option>
                    <option value="evening-5pm-8pm">Evening (5:00 PM - 8:00 PM)</option>
                    <option value="flexible">Flexible - Any time</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about any specific cleaning requirements or questions you have..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <div>
                <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                  placeholder="Add any special cleaning requests or specific instructions (e.g., pet areas, carpet cleaning, balcony, etc.)."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.additionalNotes.length}/500 characters</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="smsOptIn"
                    name="smsOptIn"
                    checked={formData.smsOptIn}
                    onChange={handleInputChange}
                    className="mt-1 h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded cursor-pointer"
                  />
                  <div className="flex-1">
                    <label htmlFor="smsOptIn" className="text-sm font-medium text-gray-700 cursor-pointer">
                      SMS Opt In
                    </label>
                    <p className="text-xs text-gray-600 mt-1">
                      Opt in to receive SMS notifications, alerts & occasional marketing communication.
                    </p>
                    <div className="mt-3 text-xs text-gray-500 leading-relaxed">
                      <p className="mb-2">
                        By providing your phone number, you agree to receive text messages from Signature Cleaning Services about appointments, updates, and promotions.
                      </p>
                      <p className="mb-2">
                        We won't share your info with third parties for marketing.
                      </p>
                      <p className="mb-2">
                        Message frequency may vary (up to 4–6 messages/month).
                      </p>
                      <p className="mb-2">
                        Reply STOP to unsubscribe at any time.
                      </p>
                      <p className="mb-2">
                        Reply HELP for more info.
                      </p>
                      <p>
                        Message & data rates may apply.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-500 text-xl mr-3"></i>
                    <p className="text-green-700">Thank you! Your quote request has been submitted successfully. We'll contact you within 24 hours.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                    <p className="text-red-700">Sorry, there was an error submitting your request. Please try again or contact us directly.</p>
                  </div>
                </div>
              )}

              <div className="text-center">
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full md:w-auto"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Get My Free Quote
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
